import { required } from "@validations";
import { reactive, ref, watch } from "@vue/composition-api";
import { dateFormat } from "@/utils/date-format";
import {
  errorNotification,
  successNotification,
} from "@/utils/toast-notifications";
import { devices, phonePrefixes, rateOptions } from "./form-options";

const recurrenceTypes = [
  { text: "Daily", value: "daily" },
  { text: "Weekly", value: "weekly" },
  { text: "Monthly", value: "monthly" },
  { text: "Custom", value: "custom" },
];

const weekdays = [
  { value: 1, text: "Monday" },
  { value: 2, text: "Tuesday" },
  { value: 3, text: "Wednesday" },
  { value: 4, text: "Thursday" },
  { value: 5, text: "Friday" },
  { value: 6, text: "Saturday" },
  { value: 7, text: "Sunday" },
];

export default function usePromoCodeForm(props, ctx) {
  const promoCodeFormRef = ref(null);
  const canModifyPromoCode = ref(true);
  const targets = reactive({
    devices: [],
    phones: [],
    users: [],
    // Contains arrays of pickups
    pickup: [],
    // Contains arrays of dropoffs
    dropoff: [],
    groups: [],
    card: "",
  });
  const promoCode = ref({
    code: null,
    effective_date_time: null,
    expiry_date_time: null,
    active: false,
    budgeted_amount: null,
    first_trips_limit: undefined,
    current_amount: 0,
    criteria: {
      is_cashback: false,
      is_bogo_promo: false,
      is_delay_discount: false,
      first_trip_only: false,
      is_extra_payment: false,
      include_drop_zone_fees: false,
      minimum_amount: null,
      services: [],
      retry: {
        per_user: null,
        max_count: null,
        per_device: null,
      },
      value: {
        rate: "percentage",
        amount: null,
        max_amount: null,
        slices: [],
      },
      targets: [],
      recurrence: {
        is_recurring: false,
        type: "daily",
        interval: null,
        usage_limit_per_period: null,
        valid_days_of_week: [],
        valid_hours_start: null,
        valid_hours_end: null,
      },
    },
  });

  const effectiveDate = ref(null);
  const effectiveTime = ref(null);
  watch(
    [effectiveDate, effectiveTime],
    ([dateVal, timeVal]) => {
      if (dateVal && timeVal) {
        promoCode.value.effective_date_time = new Date(
          `${dateFormat(dateVal)} ${timeVal}`
        );
      }
    },
    { immediate: true }
  );

  const expiryDate = ref(null);
  const expiryTime = ref(null);
  watch(
    [expiryDate, expiryTime],
    ([dateVal, timeVal]) => {
      if (dateVal && timeVal) {
        promoCode.value.expiry_date_time = new Date(
          `${dateFormat(dateVal)} ${timeVal}`
        );
      }
    },
    { immediate: true }
  );

  // Timepicker refs for valid hours
  const validHoursStartTime = ref(null);
  const validHoursEndTime = ref(null);

  // Functions to format time to HH:MM format
  const updateValidHoursStart = (timeValue) => {
    if (timeValue) {
      // Ensure the time is in HH:MM format
      const [hours, minutes] = timeValue.split(":");
      promoCode.value.criteria.recurrence.valid_hours_start = `${hours.padStart(
        2,
        "0"
      )}:${(minutes || "00").padStart(2, "0")}`;
    } else {
      promoCode.value.criteria.recurrence.valid_hours_start = null;
    }
  };

  const updateValidHoursEnd = (timeValue) => {
    if (timeValue) {
      // Ensure the time is in HH:MM format
      const [hours, minutes] = timeValue.split(":");
      promoCode.value.criteria.recurrence.valid_hours_end = `${hours.padStart(
        2,
        "0"
      )}:${(minutes || "00").padStart(2, "0")}`;
    } else {
      promoCode.value.criteria.recurrence.valid_hours_end = null;
    }
  };

  const fetchingPromo = ref(false);

  if (!props.isNew) {
    fetchingPromo.value = true;
    ctx.root.$store
      .dispatch("promoCodes/getPromoCode", ctx.root.$route.params.id)
      .then((res) => {
        const fetchedData = res.data.data;
        // Merge fetched data with default structure to preserve defaults for missing fields
        promoCode.value = {
          ...promoCode.value,
          ...fetchedData,
          criteria: {
            ...promoCode.value.criteria,
            ...fetchedData.criteria,
            recurrence: {
              ...promoCode.value.criteria.recurrence,
              ...(fetchedData.criteria?.recurrence || {}),
            },
            retry: {
              ...promoCode.value.criteria.retry,
              ...(fetchedData.criteria?.retry || {}),
            },
            value: {
              ...promoCode.value.criteria.value,
              ...(fetchedData.criteria?.value || {}),
            },
          },
        };
        if (promoCode.value?.criteria?.targets.length) {
          promoCode.value.criteria.targets.forEach((target) => {
            if (target.target_type === "payment_method")
              targets.card = target.card_type;
            if (target.target_type === "phone")
              targets.phones = target.phones.map((el) => el.prefix);
            if (target.target_type === "device")
              targets.devices = target.devices.map((el) => el.name);
            if (target.target_type === "group")
              targets.groups = target.groups.map((el) => el.id);
            if (target.target_type === "user") {
              targets.users = target.users.map((usr) => ({
                externalId: usr.external_id,
                phoneNumber: usr.phone_number,
                firstName: usr?.first_name,
                lastName: usr?.last_name,
                referenceId: usr?.reference_id,
                referenceType: usr?.reference_type,
              }));
            }
            if (target.target_type === "multi_geofence") {
              target.multi_geofence.forEach((geo) => {
                if (geo.pickup.length) {
                  targets.pickup.push([
                    ...geo.pickup.map((el) => ({
                      lat: +el.lat,
                      lng: +el.lon,
                    })),
                  ]);
                }
                if (geo.dropoff.length) {
                  targets.dropoff.push([
                    ...geo.dropoff.map((el) => ({
                      lat: +el.lat,
                      lng: +el.lon,
                    })),
                  ]);
                }
              });
            }
          });
        }
        effectiveDate.value = promoCode.value.effective_date_time;
        expiryDate.value = promoCode.value.expiry_date_time;
        effectiveTime.value = new Date(
          promoCode.value.effective_date_time
        ).toLocaleTimeString("en", { timeStyle: "short", hour12: false });
        expiryTime.value = new Date(
          promoCode.value.expiry_date_time
        ).toLocaleTimeString("en", { timeStyle: "short", hour12: false });

        // Convert hour numbers or HH:MM format back to time format for timepickers
        if (promoCode.value.criteria.recurrence.valid_hours_start !== null) {
          const startValue =
            promoCode.value.criteria.recurrence.valid_hours_start;
          // Check if it's already in HH:MM format, otherwise treat as hour number
          if (typeof startValue === "string" && startValue.includes(":")) {
            validHoursStartTime.value = startValue;
          } else {
            // Legacy: convert hour number to HH:MM
            validHoursStartTime.value = `${startValue
              .toString()
              .padStart(2, "0")}:00`;
          }
        }
        if (promoCode.value.criteria.recurrence.valid_hours_end !== null) {
          const endValue = promoCode.value.criteria.recurrence.valid_hours_end;
          // Check if it's already in HH:MM format, otherwise treat as hour number
          if (typeof endValue === "string" && endValue.includes(":")) {
            validHoursEndTime.value = endValue;
          } else {
            // Legacy: convert hour number to HH:MM
            validHoursEndTime.value = `${endValue
              .toString()
              .padStart(2, "0")}:00`;
          }
        }
      })
      .catch(() => {
        errorNotification("Failed");
        ctx.root.$router.push("/promo-codes");
      })
      .finally(() => {
        fetchingPromo.value = false;
      });
  }

  const submitPromoCode = async () => {
    const valid = await promoCodeFormRef.value.validate();
    if (!valid) return;
    if (!promoCode.value.criteria.services.length) {
      errorNotification("Please select a service");
      return;
    }

    promoCode.value.criteria.targets = [];

    if (targets.devices.length) {
      promoCode.value.criteria.targets.push({
        target_type: "device",
        devices: targets.devices.map((el) => ({
          name: el,
        })),
      });
    }
    if (targets.phones.length) {
      promoCode.value.criteria.targets.push({
        target_type: "phone",
        phones: targets.phones.map((el) => ({
          prefix: el,
        })),
      });
    }
    if (targets.groups.length) {
      promoCode.value.criteria.targets.push({
        target_type: "group",
        groups: targets.groups.map((el) => ({ id: el })),
      });
    }
    if (targets.users.length) {
      promoCode.value.criteria.targets.push({
        target_type: "user",
        users: targets.users.map((el) => ({
          external_id: el.externalId,
          first_name: el.firstName,
          last_name: el.lastName,
          phone_number: el.phoneNumber,
        })),
      });
    }
    if (targets.pickup.length || targets.dropoff.length) {
      promoCode.value.criteria.targets.push({
        target_type: "multi_geofence",
        multi_geofence: [
          ...targets.pickup.map((polygon) => ({
            target_type: "geofence",
            pickup: polygon.map((el) => ({
              lat: el.lat,
              lon: el.lng,
            })),
          })),
          ...targets.dropoff.map((polygon) => ({
            target_type: "geofence",
            dropoff: polygon.map((el) => ({
              lat: el.lat,
              lon: el.lng,
            })),
          })),
        ],
      });
    }
    if (targets.card) {
      promoCode.value.criteria.targets.push({
        target_type: "payment_method",
        card_type: targets.card,
      });
    }
    try {
      const payloadTemp = { ...promoCode.value };
      if (promoCode.value.criteria.value.slices.length) {
        payloadTemp.criteria.value.amount = 0;
      }
      if (props.isNew) {
        await ctx.root.$store.dispatch(
          "promoCodes/createPromoCode",
          payloadTemp
        );
      } else {
        await ctx.root.$store.dispatch("promoCodes/patchPromoCode", {
          id: ctx.root.$route.params.id,
          data: payloadTemp,
        });
      }
      successNotification("Success");
      ctx.root.$router.push("/promo-codes");
    } catch (error) {
      const msg = error?.response?.data?.error?.detail;
      errorNotification(msg || "Failed");
    }
  };
  return {
    recurrenceTypes,
    weekdays,
    promoCode,
    rateOptions,
    devices,
    phonePrefixes,
    required,
    effectiveDate,
    effectiveTime,
    canModifyPromoCode,
    expiryDate,
    expiryTime,
    submitPromoCode,
    promoCodeFormRef,
    targets,
    fetchingPromo,
    validHoursStartTime,
    validHoursEndTime,
    updateValidHoursStart,
    updateValidHoursEnd,
  };
}
